import { useEffect, useState } from "react";
import axios from "../../axios/AxiosInstance";
import { apiErrorMessage } from "../../utils/apiError";

const idOf = (value) => String(value?._id || value || "");
const frenchDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("fr-FR", { timeZone: "Europe/Paris", day: "2-digit", month: "2-digit", year: "numeric" })
    : "Date non renseignée";
};
const statusStyle = (status) =>
  ["Recruté", "Sélectionné"].includes(status)
    ? "bg-emerald-50 text-emerald-700"
    : status === "Refusé"
      ? "bg-rose-50 text-rose-700"
      : "bg-blue-50 text-blue-700";
export default function OpportunitiesSection({ candidateId }) {
  const [rows, setRows] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    async function load() {
      try {
        const [{ data }, directory] = await Promise.all([
          axios.get("annonces"),
          axios
            .get("client/freelance")
            .then(({ data }) => (Array.isArray(data) ? data : []))
            .catch(() => []),
        ]);
        if (!Array.isArray(data)) throw new Error("Format d’annonces invalide");
        const applications = data.flatMap((offer) =>
          (Array.isArray(offer.candidats) ? offer.candidats : [])
            .filter(
              (entry) =>
                candidateId &&
                idOf(entry.candidateId || entry) === String(candidateId),
            )
            .map((entry, index) => ({
              offer,
              status: entry.status || "Nouveau",
              date: entry.date,
              source:
                entry.source ||
                (typeof entry.candidateId === "object"
                  ? entry.candidateId?.source
                  : entry.source),
              key: `${offer._id}-${index}`,
            })),
        );
        applications.sort((a, b) =>
          String(b.date || "").localeCompare(String(a.date || "")),
        );
        if (active) {
          setRows(applications);
          setClients(directory);
        }
      } catch (error) {
        const detail = apiErrorMessage(error, "Impossible de charger les candidatures. Réessayez.");
        if (active && detail) setError(detail);
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => {
      active = false;
    };
  }, [candidateId, revision]);
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Candidatures</h2>
          <p className="mt-1 text-sm text-slate-500">
            Toutes les offres auxquelles le candidat a postulé.
          </p>
        </div>
        <button
          disabled={loading}
          onClick={() => setRevision((value) => value + 1)}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50"
        >
          Actualiser
        </button>
      </div>
      {loading ? (
        <p role="status">Chargement des candidatures…</p>
      ) : error ? (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      ) : !rows.length ? (
        <p className="rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-500">
          Aucune candidature enregistrée pour ce candidat.
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-500">
            {rows.length} candidature{rows.length > 1 ? "s" : ""}
          </p>
          {rows.map(({ offer, status, date, source, key }) => (
            <article
              key={key}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="text-sm font-semibold text-slate-900">
                  {offer.title || "Poste non renseigné"}
                </h3>
                <span
                  className={`rounded-full px-2 py-0.5 text-xs font-semibold ${statusStyle(status)}`}
                >
                  {status}
                </span>
              </div>
              <dl className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs">
                {[
                  [
                    "Client",
                    offer.client?.companyName ||
                      clients.find(
                        (client) => idOf(client) === idOf(offer.client),
                      )?.companyName ||
                      offer.clientName ||
                      "Non renseigné",
                  ],
                  ["Lieu", offer.localisation || offer.town || "Non renseigné"],
                  ["Contrat", offer.jobType || "Non renseigné"],
                  ["Candidature du", frenchDate(date)],
                  ["Origine", source || "Non renseignée"],
                ].map(([label, value]) => (
                  <div key={label} className="flex gap-1.5">
                    <dt className="text-slate-500">{label}</dt>
                    <dd className="break-words text-slate-900">{value}</dd>
                  </div>
                ))}
              </dl>
              {offer.description && (
                <details className="mt-1 text-xs">
                  <summary className="cursor-pointer text-blue-700">
                    Détails de l’offre
                  </summary>
                  <p className="mt-2 whitespace-pre-wrap break-words text-slate-700">
                    {offer.description}
                  </p>
                </details>
              )}
            </article>
          ))}
        </>
      )}
    </div>
  );
}
